import React, { useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  MapPin, Clock, Users, Zap, Search, CheckCircle2, XCircle,
  Wifi, Activity, ChevronRight, Phone, QrCode, BarChart3, ScanLine,
  AlertTriangle, Ban, Ticket,
} from "lucide-react";

const COURT_COLORS = [
  { bg: "from-teal-500/20 to-teal-600/5", border: "border-teal-500/40", text: "text-teal-400", glow: "shadow-teal-500/20" },
  { bg: "from-sky-500/20 to-sky-600/5", border: "border-sky-500/40", text: "text-sky-400", glow: "shadow-sky-500/20" },
  { bg: "from-violet-500/20 to-violet-600/5", border: "border-violet-500/40", text: "text-violet-400", glow: "shadow-violet-500/20" },
  { bg: "from-amber-500/20 to-amber-600/5", border: "border-amber-500/40", text: "text-amber-400", glow: "shadow-amber-500/20" },
  { bg: "from-rose-500/20 to-rose-600/5", border: "border-rose-500/40", text: "text-rose-400", glow: "shadow-rose-500/20" },
  { bg: "from-lime-500/20 to-lime-600/5", border: "border-lime-500/40", text: "text-lime-400", glow: "shadow-lime-500/20" },
];

function LiveClock() {
  const [time, setTime] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const hh = time.getHours().toString().padStart(2, "0");
  const mm = time.getMinutes().toString().padStart(2, "0");
  const ss = time.getSeconds().toString().padStart(2, "0");
  return (
    <div className="flex items-center gap-2 font-mono">
      <span className="text-4xl md:text-5xl font-bold text-[#F8FAFC] tracking-tight">{hh}:{mm}</span>
      <span className="text-lg text-slate-500 mt-2">{ss}</span>
    </div>
  );
}

function CourtCard({ court, bookings, color, onCheckin }) {
  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const todayStr = now.toISOString().slice(0, 10);
  const todayBookings = bookings.filter(b => b.date === todayStr && b.status !== "cancelled");
  const currentBooking = todayBookings.find(b => {
    const [sh, sm] = b.start_time.split(":").map(Number);
    const [eh, em] = b.end_time.split(":").map(Number);
    return nowMin >= sh * 60 + sm && nowMin < eh * 60 + em;
  });
  const nextBooking = todayBookings
    .filter(b => { const [h, m] = b.start_time.split(":").map(Number); return h * 60 + m > nowMin; })
    .sort((a, b) => a.start_time.localeCompare(b.start_time))[0];

  const isActive = !!currentBooking;
  const progressPercent = currentBooking ? (() => {
    const [sh, sm] = currentBooking.start_time.split(":").map(Number);
    const [eh, em] = currentBooking.end_time.split(":").map(Number);
    const total = (eh * 60 + em) - (sh * 60 + sm);
    const elapsed = nowMin - (sh * 60 + sm);
    return Math.min(100, Math.max(0, (elapsed / total) * 100));
  })() : 0;

  return (
    <div
      className={`relative rounded-2xl border-2 ${isActive ? color.border : "border-[#334155]"} bg-gradient-to-br ${isActive ? color.bg : "from-[#1e293b] to-[#0f172a]"} p-5 transition-all duration-500 ${isActive ? `shadow-lg ${color.glow}` : ""}`}
      data-testid={`court-card-${court.id}`}
    >
      {/* Court status indicator */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${isActive ? "bg-lime-400 animate-pulse" : "bg-slate-600"}`} />
          <span className="font-heading text-base font-bold text-[#F8FAFC]">{court.name}</span>
        </div>
        {isActive && (
          <span className="text-[10px] bg-lime-500/20 text-lime-400 px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider">
            Live
          </span>
        )}
      </div>

      {/* 2D Court Visual */}
      <div className={`relative h-28 rounded-xl border ${isActive ? "border-lime-500/20 bg-lime-500/5" : "border-[#334155] bg-[#0f172a]/50"} overflow-hidden mb-3`}>
        {/* Court lines */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-[1px] h-full bg-white/10" />
        </div>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full border border-white/10" />
        <div className="absolute top-0 left-0 w-8 h-8 border-b border-r border-white/10 rounded-br-lg" />
        <div className="absolute top-0 right-0 w-8 h-8 border-b border-l border-white/10 rounded-bl-lg" />
        <div className="absolute bottom-0 left-0 w-8 h-8 border-t border-r border-white/10 rounded-tr-lg" />
        <div className="absolute bottom-0 right-0 w-8 h-8 border-t border-l border-white/10 rounded-tl-lg" />

        {isActive ? (
          <>
            {/* Animated players */}
            <div className="absolute top-4 left-6 w-5 h-5 rounded-full bg-teal-400 animate-bounce shadow-lg shadow-teal-400/50" style={{ animationDelay: "0s", animationDuration: "2s" }} />
            <div className="absolute bottom-4 right-6 w-5 h-5 rounded-full bg-amber-400 animate-bounce shadow-lg shadow-amber-400/50" style={{ animationDelay: "0.5s", animationDuration: "2.2s" }} />
            {/* Ball */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-2.5 h-2.5 rounded-full bg-lime-300 animate-ping" />
            {/* Player name */}
            <div className="absolute bottom-2 left-0 right-0 text-center">
              <span className="text-[10px] text-white/70 bg-black/30 px-2 py-0.5 rounded">
                {currentBooking.customer_name || "Khách"}
              </span>
            </div>
          </>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-xs text-slate-600">Trống</span>
          </div>
        )}

        {/* Progress bar */}
        {isActive && (
          <div className="absolute bottom-0 left-0 h-1 bg-lime-500/30 w-full">
            <div className="h-full bg-lime-400 transition-all duration-1000" style={{ width: `${progressPercent}%` }} />
          </div>
        )}
      </div>

      {/* Current booking info */}
      {isActive && currentBooking && (
        <div className="space-y-1.5 mb-3">
          <div className="flex items-center gap-1.5 text-xs">
            <Users className="w-3 h-3 text-slate-500" />
            <span className="text-[#F8FAFC] font-medium truncate">{currentBooking.customer_name}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs">
            <Clock className="w-3 h-3 text-slate-500" />
            <span className="text-slate-400">{currentBooking.start_time} - {currentBooking.end_time}</span>
            <span className="text-[9px] text-lime-400 ml-auto">{Math.round(progressPercent)}%</span>
          </div>
        </div>
      )}

      {/* Next booking */}
      {!isActive && nextBooking && (
        <div className="flex items-center gap-1.5 text-xs text-slate-500 mb-3">
          <ChevronRight className="w-3 h-3" />
          <span>Kế tiếp: {nextBooking.start_time} — {nextBooking.customer_name || "Khách"}</span>
        </div>
      )}

      {/* Check-in button */}
      <Button
        size="sm"
        onClick={() => onCheckin(court)}
        className={`w-full text-xs font-semibold gap-1.5 ${isActive ? "bg-lime-500 hover:bg-lime-400 text-[#0f172a]" : "bg-[#334155] hover:bg-[#475569] text-slate-300"}`}
        data-testid={`checkin-btn-${court.id}`}
      >
        <MapPin className="w-3.5 h-3.5" />
        Check-in
      </Button>
    </div>
  );
}

// QR Scanner Tab Component
function QRScanTab() {
  const scannerRef = useRef(null);
  const html5QrRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [manualCode, setManualCode] = useState("");
  const [scanResult, setScanResult] = useState(null);
  const [processing, setProcessing] = useState(false);

  const handleScan = useCallback(async (code) => {
    if (processing) return;
    setProcessing(true);
    setScanResult(null);
    try {
      const { data } = await api.post("/admin/checkin/scan", { ticket_code: code });
      setScanResult(data);
      if (data.success) {
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      setScanResult({ success: false, message: err.response?.data?.detail || "Lỗi hệ thống" });
      toast.error("Lỗi khi quét vé");
    } finally {
      setProcessing(false);
    }
  }, [processing]);

  const startScanner = useCallback(async () => {
    if (html5QrRef.current) return;
    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      const scanner = new Html5Qrcode("qr-reader");
      html5QrRef.current = scanner;
      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => {
          handleScan(decodedText);
        },
        () => {} // ignore errors
      );
      setScanning(true);
    } catch (err) {
      toast.error("Không thể mở camera. Vui lòng cấp quyền camera.");
    }
  }, [handleScan]);

  const stopScanner = useCallback(async () => {
    if (html5QrRef.current) {
      try {
        await html5QrRef.current.stop();
      } catch {}
      html5QrRef.current = null;
    }
    setScanning(false);
  }, []);

  useEffect(() => {
    return () => { stopScanner(); };
  }, [stopScanner]);

  const handleManualScan = () => {
    if (manualCode.trim()) {
      handleScan(manualCode.trim());
      setManualCode("");
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="qr-scan-tab">
      {/* Camera / Scanner */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-bold text-[#F8FAFC] flex items-center gap-2">
            <ScanLine className="w-4 h-4 text-lime-400" />
            Camera quét QR
          </h3>
          <Button
            onClick={scanning ? stopScanner : startScanner}
            size="sm"
            className={scanning ? "bg-red-500 hover:bg-red-600 text-white" : "bg-lime-500 hover:bg-lime-400 text-[#0f172a]"}
            data-testid="toggle-scanner-btn"
          >
            {scanning ? "Tắt camera" : "Bật camera"}
          </Button>
        </div>
        <div id="qr-reader" ref={scannerRef} className="rounded-lg overflow-hidden bg-[#0f172a] min-h-[250px]" data-testid="qr-reader" />

        {/* Manual code input */}
        <div className="mt-3 flex gap-2">
          <Input
            value={manualCode}
            onChange={e => setManualCode(e.target.value.toUpperCase())}
            placeholder="Nhập mã vé (VD: KX-XXXXXXXXXX)"
            className="bg-[#0f172a] border-[#334155] text-slate-50 font-mono"
            onKeyDown={e => e.key === "Enter" && handleManualScan()}
            data-testid="manual-code-input"
          />
          <Button onClick={handleManualScan} disabled={processing || !manualCode.trim()}
            className="bg-lime-500 hover:bg-lime-400 text-[#0f172a] font-semibold shrink-0"
            data-testid="manual-scan-btn"
          >
            Quét
          </Button>
        </div>
      </div>

      {/* Scan Result */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4">
        <h3 className="text-sm font-bold text-[#F8FAFC] mb-3 flex items-center gap-2">
          <Ticket className="w-4 h-4 text-lime-400" />
          Kết quả
        </h3>
        {!scanResult ? (
          <div className="flex flex-col items-center justify-center py-12 text-slate-500">
            <QrCode className="w-12 h-12 mb-3 opacity-20" />
            <p className="text-sm">Quét QR hoặc nhập mã vé</p>
          </div>
        ) : scanResult.success ? (
          <div className="space-y-3" data-testid="scan-result-success">
            <div className="flex items-center gap-2 text-lime-400">
              <CheckCircle2 className="w-6 h-6" />
              <span className="text-lg font-bold">Check-in thành công!</span>
            </div>
            {scanResult.ticket && (
              <div className="bg-[#0f172a] rounded-lg p-3 space-y-2 text-sm">
                <div className="flex justify-between"><span className="text-slate-400">Mã vé</span><span className="text-slate-200 font-mono">{scanResult.ticket.code}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Khách hàng</span><span className="text-slate-200">{scanResult.ticket.customer_name}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">SĐT</span><span className="text-slate-200">{scanResult.ticket.customer_phone}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Sân</span><span className="text-teal-400">{scanResult.ticket.court_name}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Giờ chơi</span><span className="text-slate-200 font-bold">{scanResult.ticket.start_time} - {scanResult.ticket.end_time}</span></div>
                <div className="flex justify-between"><span className="text-slate-400">Ngày</span><span className="text-slate-200">{scanResult.ticket.play_date}</span></div>
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-3" data-testid="scan-result-fail">
            <div className="flex items-center gap-2 text-red-400">
              <XCircle className="w-6 h-6" />
              <span className="text-lg font-bold">Thất bại</span>
            </div>
            <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3">
              <p className="text-sm text-red-400">{scanResult.message}</p>
              {scanResult.reason && <p className="text-xs text-red-400/60 mt-1">Mã lỗi: {scanResult.reason}</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Check-in Stats Tab Component
function CheckinStatsTab() {
  const todayStr = new Date().toISOString().slice(0, 10);
  const [selectedDate, setSelectedDate] = useState(todayStr);

  const { data: statsData } = useQuery({
    queryKey: ["checkin-stats", selectedDate],
    queryFn: () => api.get(`/admin/checkin/stats?play_date=${selectedDate}`).then(r => r.data),
    refetchInterval: 15000,
  });
  const stats = statsData || {};

  const { data: logsData } = useQuery({
    queryKey: ["checkin-logs", selectedDate],
    queryFn: () => api.get(`/admin/checkin/logs?play_date=${selectedDate}`).then(r => r.data),
    refetchInterval: 15000,
  });
  const logs = logsData?.logs || [];

  const { data: ticketsData } = useQuery({
    queryKey: ["admin-tickets", selectedDate],
    queryFn: () => api.get(`/admin/tickets?play_date=${selectedDate}`).then(r => r.data),
    refetchInterval: 15000,
  });
  const tickets = ticketsData?.tickets || [];

  const handleManualCheckin = async (ticketId) => {
    try {
      await api.post("/admin/checkin/manual", { ticket_id: ticketId });
      toast.success("Check-in thủ công thành công");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Lỗi");
    }
  };

  const statCards = [
    { label: "Tổng vé", value: stats.total_tickets || 0, color: "text-[#F8FAFC]", icon: Ticket },
    { label: "Đã check-in", value: stats.checked_in || 0, color: "text-lime-400", icon: CheckCircle2 },
    { label: "Chưa check-in", value: stats.not_checked_in || 0, color: "text-amber-400", icon: Clock },
    { label: "Hết hạn", value: stats.expired || 0, color: "text-red-400", icon: AlertTriangle },
    { label: "Đã huỷ", value: stats.cancelled || 0, color: "text-slate-500", icon: Ban },
    { label: "Quét lỗi", value: stats.failed_scans || 0, color: "text-red-400", icon: XCircle },
  ];

  const STATUS_BADGE = {
    ACTIVE: "bg-amber-500/20 text-amber-400 border-amber-500/30",
    USED: "bg-lime-500/20 text-lime-400 border-lime-500/30",
    EXPIRED: "bg-red-500/20 text-red-400 border-red-500/30",
    CANCELLED: "bg-slate-500/20 text-slate-400 border-slate-500/30",
  };
  const STATUS_LABEL = { ACTIVE: "Chưa CI", USED: "Đã CI", EXPIRED: "Hết hạn", CANCELLED: "Đã huỷ" };

  return (
    <div className="space-y-4" data-testid="checkin-stats-tab">
      {/* Date picker */}
      <div className="flex items-center gap-3">
        <input type="date" value={selectedDate} onChange={e => setSelectedDate(e.target.value)}
          className="bg-[#1e293b] border border-[#334155] rounded-lg px-3 py-2 text-sm text-slate-200" data-testid="stats-date-picker" />
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {statCards.map(s => (
          <div key={s.label} className="bg-[#1e293b] border border-[#334155] rounded-lg p-3 text-center">
            <s.icon className={`w-5 h-5 mx-auto mb-1 ${s.color}`} />
            <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
            <div className="text-[10px] text-slate-500">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Tickets table */}
      <div className="bg-[#1e293b] border border-[#334155] rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-[#334155]">
          <h3 className="text-sm font-bold text-[#F8FAFC]">Danh sách vé ({tickets.length})</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-[#0f172a] text-slate-400">
                <th className="px-3 py-2 text-left">Mã vé</th>
                <th className="px-3 py-2 text-left">Khách hàng</th>
                <th className="px-3 py-2 text-left">SĐT</th>
                <th className="px-3 py-2 text-left">Sân</th>
                <th className="px-3 py-2 text-left">Giờ</th>
                <th className="px-3 py-2 text-left">Trạng thái</th>
                <th className="px-3 py-2 text-left">Check-in</th>
                <th className="px-3 py-2 text-left">Thao tác</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map(t => (
                <tr key={t.id} className="border-t border-[#334155]/50 hover:bg-[#334155]/10">
                  <td className="px-3 py-2 font-mono text-slate-300">{t.code}</td>
                  <td className="px-3 py-2 text-slate-200">{t.customer_name}</td>
                  <td className="px-3 py-2 text-slate-400">{t.customer_phone}</td>
                  <td className="px-3 py-2 text-teal-400">{t.court_name}</td>
                  <td className="px-3 py-2 text-slate-200 font-medium">{t.start_time}-{t.end_time}</td>
                  <td className="px-3 py-2">
                    <Badge className={`${STATUS_BADGE[t.status] || ""} text-[10px] border`}>{STATUS_LABEL[t.status] || t.status}</Badge>
                  </td>
                  <td className="px-3 py-2 text-slate-400">{t.checked_in_at ? new Date(t.checked_in_at).toLocaleTimeString("vi-VN", { hour: "2-digit", minute: "2-digit" }) : "—"}</td>
                  <td className="px-3 py-2">
                    {t.status === "ACTIVE" && (
                      <button onClick={() => handleManualCheckin(t.id)}
                        className="text-lime-400 hover:text-lime-300 text-[10px] font-semibold" data-testid={`manual-ci-${t.id}`}>
                        CI thủ công
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {tickets.length === 0 && (
                <tr><td colSpan={8} className="px-3 py-8 text-center text-slate-500">Không có vé nào</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function CheckinPage() {
  const queryClient = useQueryClient();
  const [checkinOpen, setCheckinOpen] = useState(false);
  const [selectedCourt, setSelectedCourt] = useState(null);
  const [phoneInput, setPhoneInput] = useState("");
  const [checking, setChecking] = useState(false);
  const [checkinResult, setCheckinResult] = useState(null);
  const [recentCheckins, setRecentCheckins] = useState([]);
  const [activeTab, setActiveTab] = useState("courts");
  const todayStr = new Date().toISOString().slice(0, 10);

  const { data: courtsData } = useQuery({
    queryKey: ["courts"],
    queryFn: () => api.get("/courts").then(r => r.data),
    refetchInterval: 30000,
  });
  const { data: bookingsData } = useQuery({
    queryKey: ["bookings-today", todayStr],
    queryFn: () => api.get(`/bookings?date=${todayStr}`).then(r => r.data),
    refetchInterval: 15000,
  });

  const courts = courtsData?.courts || [];
  const bookings = bookingsData?.bookings || [];

  const now = new Date();
  const nowMin = now.getHours() * 60 + now.getMinutes();
  const activeCourts = courts.filter(c => {
    return bookings.some(b => {
      if (b.court_id !== c.id || b.status === "cancelled" || b.date !== todayStr) return false;
      const [sh, sm] = b.start_time.split(":").map(Number);
      const [eh, em] = b.end_time.split(":").map(Number);
      return nowMin >= sh * 60 + sm && nowMin < eh * 60 + em;
    });
  });

  const openCheckin = (court) => {
    setSelectedCourt(court);
    setPhoneInput("");
    setCheckinResult(null);
    setCheckinOpen(true);
  };

  const handleCheckin = async () => {
    if (!phoneInput.trim()) return;
    setChecking(true);
    setCheckinResult(null);
    try {
      // Find matching booking for this court today
      const courtBookings = bookings.filter(b =>
        b.court_id === selectedCourt.id && b.date === todayStr && b.status !== "cancelled"
      );
      const match = courtBookings.find(b => {
        const phone = b.customer_phone || "";
        return phone.includes(phoneInput.trim()) || phoneInput.trim().includes(phone);
      });

      if (match) {
        setCheckinResult({ success: true, booking: match });
        const entry = {
          name: match.customer_name,
          phone: match.customer_phone,
          court: selectedCourt.name,
          time: `${match.start_time}-${match.end_time}`,
          checkedAt: new Date().toLocaleTimeString("vi-VN"),
        };
        setRecentCheckins(prev => [entry, ...prev.slice(0, 9)]);
        toast.success(`Check-in: ${match.customer_name} - ${selectedCourt.name}`);
      } else {
        setCheckinResult({ success: false, message: "Không tìm thấy booking khớp SĐT" });
      }
    } catch (err) {
      setCheckinResult({ success: false, message: "Lỗi hệ thống" });
    } finally {
      setChecking(false);
    }
  };

  // Auto-refresh
  useEffect(() => {
    const interval = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: ["bookings-today"] });
    }, 30000);
    return () => clearInterval(interval);
  }, [queryClient]);

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="checkin-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-lime-500/30 to-teal-500/20 flex items-center justify-center border border-lime-500/30">
            <Zap className="w-6 h-6 text-lime-400" />
          </div>
          <div>
            <h1 className="font-heading text-lg font-bold text-[#F8FAFC]">Check-in Sân</h1>
            <p className="text-slate-500 text-[11px] flex items-center gap-1.5">
              <Wifi className="w-3 h-3 text-lime-400 animate-pulse" />
              Realtime — {todayStr}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 bg-[#1e293b] border border-[#334155] rounded-xl px-4 py-2">
            <div className="text-center">
              <div className="text-lg font-bold text-lime-400">{activeCourts.length}</div>
              <div className="text-[9px] text-slate-500 uppercase tracking-wider">Sân hoạt động</div>
            </div>
            <div className="w-px h-8 bg-[#334155]" />
            <div className="text-center">
              <div className="text-lg font-bold text-[#F8FAFC]">{courts.length}</div>
              <div className="text-[9px] text-slate-500 uppercase tracking-wider">Tổng sân</div>
            </div>
            <div className="w-px h-8 bg-[#334155]" />
            <div className="text-center">
              <div className="text-lg font-bold text-amber-400">{recentCheckins.length}</div>
              <div className="text-[9px] text-slate-500 uppercase tracking-wider">Check-in</div>
            </div>
          </div>
          <LiveClock />
        </div>
      </div>

      {/* Tab buttons */}
      <div className="flex gap-2" data-testid="checkin-tabs">
        {[
          { key: "courts", label: "Sân", icon: MapPin },
          { key: "qr", label: "Quét QR", icon: QrCode },
          { key: "stats", label: "Thống kê", icon: BarChart3 },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.key ? "bg-lime-500/20 text-lime-400 border border-lime-500/30" : "bg-[#1e293b] text-slate-400 border border-[#334155] hover:text-slate-200"
            }`}
            data-testid={`tab-${tab.key}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* TAB: Courts */}
      {activeTab === "courts" && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {courts.map((court, i) => (
              <CourtCard
                key={court.id}
                court={court}
                bookings={bookings.filter(b => b.court_id === court.id)}
                color={COURT_COLORS[i % COURT_COLORS.length]}
                onCheckin={openCheckin}
              />
            ))}
          </div>
          {recentCheckins.length > 0 && (
            <div className="bg-[#1e293b] border border-[#334155] rounded-xl p-4" data-testid="recent-checkins">
              <h3 className="text-sm font-bold text-[#F8FAFC] mb-3 flex items-center gap-2">
                <Activity className="w-4 h-4 text-lime-400" />
                Check-in gần đây
              </h3>
              <div className="space-y-2">
                {recentCheckins.map((c, i) => (
                  <div key={i} className="flex items-center gap-3 bg-[#0f172a] rounded-lg px-3 py-2 text-xs">
                    <CheckCircle2 className="w-4 h-4 text-lime-400 shrink-0" />
                    <span className="font-medium text-[#F8FAFC]">{c.name}</span>
                    <span className="text-slate-500">—</span>
                    <span className="text-teal-400">{c.court}</span>
                    <span className="text-slate-600">{c.time}</span>
                    <span className="ml-auto text-slate-500">{c.checkedAt}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* TAB: QR Scan */}
      {activeTab === "qr" && <QRScanTab />}

      {/* TAB: Stats */}
      {activeTab === "stats" && <CheckinStatsTab />}

      {/* Check-in Dialog */}
      <Dialog open={checkinOpen} onOpenChange={setCheckinOpen}>
        <DialogContent className="bg-[#1e293b] border-[#334155] sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-[#F8FAFC] flex items-center gap-2">
              <MapPin className="w-5 h-5 text-lime-400" />
              Check-in {selectedCourt?.name}
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-xs">
              Nhập SĐT khách hàng để check-in
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input
                value={phoneInput}
                onChange={(e) => setPhoneInput(e.target.value)}
                placeholder="Số điện thoại..."
                className="pl-10 bg-[#0f172a] border-[#334155] text-slate-200 text-lg h-12"
                autoFocus
                onKeyDown={(e) => e.key === "Enter" && handleCheckin()}
                data-testid="checkin-phone-input"
              />
            </div>

            {checkinResult && (
              <div className={`rounded-lg p-3 border ${checkinResult.success ? "border-lime-500/30 bg-lime-500/10" : "border-red-500/30 bg-red-500/10"}`}>
                {checkinResult.success ? (
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5 text-lime-400" />
                    <div>
                      <p className="text-sm font-bold text-lime-400">Check-in OK!</p>
                      <p className="text-xs text-slate-400">{checkinResult.booking.customer_name} — {checkinResult.booking.start_time}-{checkinResult.booking.end_time}</p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-red-400" />
                    <p className="text-sm text-red-400">{checkinResult.message}</p>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setCheckinOpen(false)} className="text-slate-400">Đóng</Button>
            <Button
              onClick={handleCheckin}
              disabled={checking || !phoneInput.trim()}
              className="bg-lime-500 hover:bg-lime-400 text-[#0f172a] font-bold gap-1.5"
              data-testid="checkin-confirm-btn"
            >
              {checking ? "Đang kiểm tra..." : "Check-in"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
